import { forwardRef, useEffect, useRef } from "react";
import type { InputHTMLAttributes, ReactNode } from "react";
import "./Checkbox.css";

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
  label?: ReactNode;
  indeterminate?: boolean;
  invalid?: boolean;
}

const cx = (...classes: Array<string | false | undefined>) => classes.filter(Boolean).join(" ");

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, indeterminate = false, invalid = false, className, disabled, id, ...rest },
  ref,
) {
  const localRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (localRef.current) localRef.current.indeterminate = indeterminate;
  }, [indeterminate]);

  const setRef = (node: HTMLInputElement | null) => {
    localRef.current = node;
    if (typeof ref === "function") ref(node);
    else if (ref) (ref as React.MutableRefObject<HTMLInputElement | null>).current = node;
  };

  return (
    <label
      className={cx(
        "go4-checkbox",
        invalid && "go4-checkbox--invalid",
        disabled && "go4-checkbox--disabled",
        className,
      )}
    >
      <input
        ref={setRef}
        id={id}
        type="checkbox"
        disabled={disabled}
        aria-invalid={invalid || undefined}
        className="go4-checkbox__input"
        {...rest}
      />
      <span
        className="go4-checkbox__box"
        aria-hidden="true"
        data-indeterminate={indeterminate || undefined}
      >
        {indeterminate ? (
          <svg className="go4-checkbox__icon" viewBox="0 0 24 24">
            <path d="M5 12h14" />
          </svg>
        ) : (
          <svg className="go4-checkbox__icon" viewBox="0 0 24 24">
            <path d="M4 12.5l5 5L20 6.5" />
          </svg>
        )}
      </span>
      {label ? <span className="go4-checkbox__label">{label}</span> : null}
    </label>
  );
});
